import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFiscalStatus, getSettings } from '../lib/api';
import { Toaster } from 'sonner';
import { openedBuild } from '../lib/releases';
import {
  LayoutDashboard, BarChart2, ChevronRight, Users, Package,
  Tag, Monitor, Settings, LogOut, Store, Layers, Printer,
  FileText, Download, Clock, Receipt, Table2, Map, Cpu,
  CreditCard, Shield, Building2, UtensilsCrossed, ListOrdered,
  Grid3x3, SlidersHorizontal, BookOpen, Zap, ShieldAlert, Calendar,
  ChevronsUpDown, CircleDot, Sparkles, Bell, ShoppingBag, QrCode,
  Ticket, WalletCards, ScanQrCode, ClipboardList, History, Menu, X
} from 'lucide-react';

const OPERATIONS_NAV = [
  {
    path: '/admin/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
  },
  {
    section: 'Reports',
    icon: BarChart2,
    children: [
      { path: '/admin/reports',              label: 'Berichte',      icon: BarChart2 },
      { path: '/admin/reports/shifts',       label: 'Schichten',     icon: Clock },
      { path: '/admin/reports/invoices',     label: 'Belege',        icon: Receipt },
      { path: '/admin/reports/orders',       label: 'Bestellungen',  icon: FileText },
      { path: '/admin/reports/exports',      label: 'Exporte',       icon: Download },
      { path: '/admin/basic/kassenbuch',     label: 'Kassenbuch',    icon: BookOpen },
    ],
  },
];

const MANAGE_NAV = [
  {
    path: '/admin/customers',
    label: 'Customers',
    icon: Users,
  },
  {
    section: 'Artikel',
    icon: Package,
    children: [
      { path: '/admin/articles/studio',       label: 'Menü Studio',         icon: UtensilsCrossed },
      { path: '/admin/articles/products',     label: 'Artikel',             icon: Package },
      { path: '/admin/articles/groups',       label: 'Artikelgruppen',      icon: Tag },
      { path: '/admin/articles/options',      label: 'Artikeloptionen',     icon: SlidersHorizontal },
      { path: '/admin/articles/print-groups', label: 'Druckergruppen',      icon: Printer },
      { path: '/admin/articles/pricing',      label: 'Preisstufen',         icon: Layers },
      { path: '/admin/articles/courses',      label: 'Ganggruppen',         icon: BookOpen },
    ],
  },
  {
    section: 'Locations',
    icon: Store,
    children: [
      { path: '/admin/venues',                label: 'Standorte',         icon: Building2 },
      { path: '/admin/venues/sales-points',   label: 'Verkaufspunkte',    icon: Zap },
      { path: '/admin/venues/rooms',          label: 'Räume',             icon: UtensilsCrossed },
      { path: '/admin/venues/tables',         label: 'Tische',            icon: Table2 },
      { path: '/admin/venues/room-layout',    label: 'Raumplan',          icon: Map },
      { path: '/admin/venues/monitors',       label: 'Bestellmonitore',   icon: Monitor },
      { path: '/admin/venues/dispenser',      label: 'Dispenser',         icon: Cpu },
    ],
  },
  {
    section: 'Basic data',
    icon: FileText,
    children: [
      { path: '/admin/basic/company',         label: 'Unternehmen',       icon: Building2 },
      { path: '/admin/basic/tax-types',       label: 'Steuertypen',       icon: ListOrdered },
      { path: '/admin/basic/payment-methods', label: 'Zahlungsarten',     icon: CreditCard },
      { path: '/admin/basic/users',           label: 'Benutzer',          icon: Users },
      { path: '/admin/basic/user-roles',      label: 'Rollen',            icon: Shield },
      { path: '/admin/basic/fiscal',          label: 'Fiskalierung',      icon: Shield, fiscalAlert: true },
      { path: '/admin/basic/subscription',    label: 'Abonnement',        icon: CreditCard },
    ],
  },
  {
    path: '/admin/location-presence',
    label: 'Location presence',
    icon: Store,
  },
  {
    path: '/admin/app-customers',
    label: 'App customers',
    icon: Users,
  },
];

const ONLINE_ORDERING_NAV = [
  { path: '/admin/online-ordering/setup-status',  label: 'Setup status',  icon: ClipboardList },
  { path: '/admin/online-ordering/orders',        label: 'Online Orders', icon: Receipt },
  { path: '/admin/online-ordering/menus',         label: 'Menus',         icon: UtensilsCrossed },
  { path: '/admin/online-ordering/daily-menu',    label: 'Daily menu',    icon: Calendar },
  { path: '/admin/online-ordering/order-settings',label: 'Order Settings',icon: Settings },
  { path: '/admin/online-ordering/webshop',       label: 'Webshop',       icon: Store },
  { path: '/admin/online-ordering/pickup-display',label: 'Pickup display',icon: Monitor },
  { path: '/admin/online-ordering/terminals',     label: 'Terminals',     icon: QrCode },
];

const LOYALTY_NAV = [
  { path: '/admin/loyalty/cards',        label: 'Loyalty Cards', icon: WalletCards },
  { path: '/admin/loyalty/issued',       label: 'Issued Cards',  icon: CreditCard },
  { path: '/admin/loyalty/coupons',      label: 'Coupons',       icon: Ticket },
  { path: '/admin/loyalty/transactions', label: 'Transactions',  icon: ScanQrCode },
];

const APP_NAV = [
  { path: '/admin/app/notifications', label: 'Notifications', icon: Bell },
];

function NavItem({ item, depth = 0, tseAlert = false }) {
  const location = useLocation();
  const isActive = location.pathname === item.path ||
    (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
  const Icon = item.icon;
  const showAlert = item.fiscalAlert && tseAlert;

  return (
    <NavLink
      to={item.path}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] transition-all
        ${depth > 0 ? 'ml-6 py-2.5 text-sm' : ''}
        ${isActive
          ? 'bg-[#d8ebe0] text-[#082836] font-semibold shadow-sm'
          : 'text-white/72 hover:text-white hover:bg-white/6'}`}
    >
      <Icon className={`${depth > 0 ? 'w-4 h-4' : 'w-4.5 h-4.5'} shrink-0`} />
      <span className="flex-1">{item.label}</span>
      {showAlert && (
        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" title="TSE-Fehler" />
      )}
    </NavLink>
  );
}

function NavSection({ item, tseAlert = false }) {
  const location = useLocation();
  const isAnyChildActive = item.children.some(
    (c) => location.pathname === c.path || location.pathname.startsWith(c.path)
  );
  const [open, setOpen] = useState(isAnyChildActive);
  const Icon = item.icon;
  const sectionHasAlert = tseAlert && item.children.some((c) => c.fiscalAlert);

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-[15px] transition-all
          ${isAnyChildActive ? 'text-white font-semibold bg-white/4' : 'text-white/68 hover:text-white hover:bg-white/6'}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 shrink-0" />
          <span>{item.section}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {sectionHasAlert && !open && (
            <span className="w-2 h-2 rounded-full bg-red-500" />
          )}
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="mt-1 space-y-1">
          {item.children.map((child) => (
            <NavItem key={child.path} item={child} depth={1} tseAlert={tseAlert} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  const [tseAlert, setTseAlert] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyMeta, setCompanyMeta] = useState('');

  useEffect(() => {
    // Silently poll TSE status for the red-dot indicator every 5 minutes
    const check = async () => {
      try {
        const status = await getFiscalStatus();
        setTseAlert(!!status.failure_since);
      } catch {
        // Ignore — don't crash the layout if fiscal API is unavailable
      }
    };
    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCompany = async () => {
      try {
        const settings = await getSettings();
        if (!isMounted) return;
        setCompanyName(settings?.company_name || '');
        setCompanyMeta(settings?.company_city || '');
      } catch {
        // Ignore layout metadata fetch failures.
      }
    };

    loadCompany();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-[#f5f7fb] text-slate-900">
      {mobileNavOpen && <button aria-label="Navigation schließen" onClick={() => setMobileNavOpen(false)} className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" />}
      <aside id="admin-navigation" className={`${mobileNavOpen ? 'flex' : 'hidden'} fixed inset-y-0 left-0 z-40 w-[min(304px,85vw)] flex-col bg-[#082836] text-white shrink-0 overflow-hidden lg:static lg:flex lg:w-[304px]`}>
        <div className="px-4 pt-3 pb-4 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3 px-1 pb-4">
            <button onClick={() => setMobileNavOpen(false)} aria-label="Navigation schließen" className="lg:hidden"><X className="h-5 w-5" /></button>
            <div className="w-10 h-10 rounded-2xl bg-[#71c545] flex items-center justify-center shadow-sm">
              <UtensilsCrossed className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg leading-tight">FiscalPOS</h1>
              <p className="text-[11px] text-white/45 uppercase tracking-[0.18em]">Cloud Administration</p>
            </div>
          </div>

          <button
            type="button"
            className="w-full rounded-2xl border border-[#335364] bg-[#0b3143] px-4 py-3 text-left transition-colors hover:border-[#476878] hover:bg-[#11394c]"
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="truncate text-base font-semibold">
                  {companyName || user?.name || 'FiscalPOS Standort'}
                </p>
                <p className="truncate text-sm text-white/55">
                  {companyMeta || (user?.role === 'superadmin' ? 'Plattformsteuerung' : 'Manager Portal')}
                </p>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-white/55 shrink-0" />
            </div>
          </button>
        </div>

        <nav className="min-h-0 flex-1 px-4 py-4 space-y-5 overflow-y-auto">
          {user?.role === 'superadmin' && (
            <div>
              <div className="px-4 mb-2 text-[11px] font-semibold text-rose-300 uppercase tracking-[0.18em]">Superadmin</div>
              <NavLink
                to="/superadmin"
                className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-2xl text-[15px] transition-all ${isActive ? 'bg-rose-500 text-white font-semibold shadow-sm' : 'text-white/72 hover:text-white hover:bg-white/6'}`}
              >
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <span>Plattform verwalten</span>
              </NavLink>
            </div>
          )}

          <div>
            <div className="px-4 mb-2 text-[11px] font-semibold text-[#98da55] uppercase tracking-[0.18em]">
              Operations
            </div>
            {OPERATIONS_NAV.map((item, i) =>
              item.section ? (
                <NavSection key={i} item={item} tseAlert={tseAlert} />
              ) : (
                <NavItem key={item.path} item={item} tseAlert={tseAlert} />
              )
            )}
          </div>

          <div>
            <div className="px-4 mb-2 text-[11px] font-semibold text-[#98da55] uppercase tracking-[0.18em]">
              Manage
            </div>
            {MANAGE_NAV.map((item, i) =>
              item.section ? (
                <NavSection key={`manage-${i}`} item={item} tseAlert={tseAlert} />
              ) : (
                <NavItem key={item.path} item={item} tseAlert={tseAlert} />
              )
            )}
          </div>

          <div>
            <div className="px-4 mb-2 text-[11px] font-semibold text-[#98da55] uppercase tracking-[0.18em]">
              Online Ordering
            </div>
            <div className="space-y-1">
              {ONLINE_ORDERING_NAV.map((item) => (
                <NavItem key={item.path} item={item} tseAlert={tseAlert} />
              ))}
            </div>
          </div>

          <div>
            <div className="px-4 mb-2 text-[11px] font-semibold text-[#98da55] uppercase tracking-[0.18em]">
              Loyalty
            </div>
            <div className="space-y-1">
              {LOYALTY_NAV.map((item) => (
                <NavItem key={item.path} item={item} tseAlert={tseAlert} />
              ))}
            </div>
          </div>

          <div>
            <div className="px-4 mb-2 text-[11px] font-semibold text-[#98da55] uppercase tracking-[0.18em]">
              App
            </div>
            <div className="space-y-1">
              {APP_NAV.map((item) => (
                <NavItem key={item.path} item={item} tseAlert={tseAlert} />
              ))}
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-white/8 shrink-0">
          <div className="rounded-2xl bg-white/4 p-3">
            <div className="mb-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#71c545] flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{user?.name || 'Admin'}</p>
                <p className="text-[11px] text-white/50 capitalize">{user?.role || 'admin'}</p>
              </div>
              {tseAlert && <CircleDot className="w-3 h-3 text-rose-400 shrink-0" />}
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-white/72 hover:text-white hover:bg-white/6 transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span>Abmelden</span>
            </button>
            <NavLink to={user?.role === 'superadmin' ? '/superadmin/releases' : '/admin/releases'} className="mt-3 flex items-center gap-2 rounded-xl border border-white/15 px-3 py-2.5 text-xs text-white/80 hover:bg-white/10">
              <History className="h-4 w-4 shrink-0" /><span>v{openedBuild.version} · {openedBuild.environment}<span className="mt-0.5 block text-[10px] text-white/50">Versionen & Updates</span></span>
            </NavLink>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto">
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200/80 bg-white/95 px-4 py-4 backdrop-blur sm:px-8">
          <button aria-label="Navigation öffnen" aria-expanded={mobileNavOpen} aria-controls="admin-navigation" onClick={() => setMobileNavOpen(true)} className="lg:hidden"><Menu className="h-5 w-5" /></button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Manager Portal</p>
            <p className="text-sm text-slate-600">
              {companyName || 'FiscalPOS'}{companyMeta ? ` · ${companyMeta}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {tseAlert && (
              <div className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700">
                Fiskalstatus prüfen
              </div>
            )}
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f2f5ef]">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#71c545] text-white">
                <UtensilsCrossed className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>

        <Outlet />
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
